/**
 * Pure table utilities: sort rows by a column with numeric, date, or string
 * handling. Uses date-fns-tz for ISO and date-fns for long-form date parsing.
 */

import { parse } from "date-fns";
import { enUS } from "date-fns/locale";
import { toDate } from "date-fns-tz";

export interface TableCellLink {
  text: string;
  url: string;
}

/** A run of plain text or a link inside a table cell. */
export type TableCellPart = string | TableCellLink;

export const isHttpUrl = (url: string) =>
  url.startsWith("http://") || url.startsWith("https://");

/** Index of the bracket that closes the one opened just before `start`, or -1. */
const findClosingBracket = (
  value: string,
  start: number,
  open: string,
  close: string,
): number => {
  let depth = 1;
  for (let i = start; i < value.length; i++) {
    if (value[i] === open) depth++;
    else if (value[i] === close) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
};

/**
 * Splits markdown `[label](url)` links out of text. Labels may contain
 * balanced brackets (e.g. `[[Video] Headline](url)`) and URLs balanced
 * parentheses.
 */
const splitMarkdownLinks = (value: string): TableCellPart[] => {
  const parts: TableCellPart[] = [];
  let cursor = 0;
  let start = value.indexOf("[");
  while (start !== -1) {
    const labelEnd = findClosingBracket(value, start + 1, "[", "]");
    const urlStart = labelEnd + 2;
    const urlEnd =
      labelEnd > start + 1 && value[labelEnd + 1] === "("
        ? findClosingBracket(value, urlStart, "(", ")")
        : -1;
    const url = urlEnd > urlStart ? value.slice(urlStart, urlEnd) : "";
    // Non-web destinations such as `[USD](millions)` stay literal text.
    if (isHttpUrl(url)) {
      if (start > cursor) parts.push(value.slice(cursor, start));
      parts.push({ text: value.slice(start + 1, labelEnd), url });
      cursor = urlEnd + 1;
      start = value.indexOf("[", cursor);
    } else {
      start = value.indexOf("[", start + 1);
    }
  }
  if (cursor < value.length) parts.push(value.slice(cursor));
  return parts;
};

/**
 * Splits a Herald table cell into text and link parts. Cells may be plain
 * scalars, strings with markdown links, link objects ({ text, url }; legacy
 * payloads may use `href`), or arrays of these, which are joined with ", ".
 */
export const tableCellToParts = (value: unknown): TableCellPart[] => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    const parts: TableCellPart[] = [];
    for (const item of value) {
      const itemParts = tableCellToParts(item);
      if (itemParts.length === 0) continue;
      if (parts.length > 0) parts.push(", ");
      parts.push(...itemParts);
    }
    return parts;
  }
  if (typeof value === "string") return splitMarkdownLinks(value);
  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  if (typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  if (typeof record.text !== "string" || record.text.length === 0) return [];
  const url = record.url ?? record.href;
  if (typeof url === "string" && url.length > 0) {
    return [{ text: record.text, url }];
  }
  return [record.text];
};

/** Display text of a table cell, with link labels in place of links. */
export const tableCellToString = (value: unknown): string =>
  tableCellToParts(value)
    .map((part) => (typeof part === "string" ? part : part.text))
    .join("");

/** Returns the link when the whole cell is a single link object or markdown link. */
export const parseTableCellLink = (value: unknown): TableCellLink | null => {
  const parts = tableCellToParts(
    typeof value === "string" ? value.trim() : value,
  );
  const [part] = parts;
  return parts.length === 1 && typeof part === "object" ? part : null;
};

/**
 * Parses a date string (ISO 8601 or long-form e.g. "January 1, 2024") to a
 * timestamp. Returns NaN if not parseable. Uses date-fns-tz for ISO and
 * date-fns for long-form so parsing is deterministic across environments.
 */
const parseDateToTimestamp = (str: string): number => {
  const iso = toDate(str);
  if (!Number.isNaN(iso.getTime())) return iso.getTime();
  const longForm = parse(str, "MMMM d, yyyy", new Date(), { locale: enUS });
  return Number.isNaN(longForm.getTime()) ? Number.NaN : longForm.getTime();
};

/**
 * Sorts an array of records by a given key and direction. Handles numbers,
 * numeric strings (e.g. "1,200"), dates, and locale-aware string fallback.
 * Returns a new array; input is not mutated.
 */
export const sortTableItems = (
  items: ReadonlyArray<Record<string, unknown>>,
  sortKey: string | null,
  sortDir: "asc" | "desc",
): ReadonlyArray<Record<string, unknown>> => {
  if (!sortKey) return items;

  const key = sortKey;
  return [...items].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDir === "asc" ? aValue - bValue : bValue - aValue;
    }

    const aStr = tableCellToString(aValue);
    const bStr = tableCellToString(bValue);

    const aNum = Number.parseFloat(aStr.replace(/,/g, ""));
    const bNum = Number.parseFloat(bStr.replace(/,/g, ""));
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
      return sortDir === "asc" ? aNum - bNum : bNum - aNum;
    }

    const aDate = parseDateToTimestamp(aStr);
    const bDate = parseDateToTimestamp(bStr);
    if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
      return sortDir === "asc" ? aDate - bDate : bDate - aDate;
    }

    return sortDir === "asc"
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });
};
